import React, { useState, useRef, useCallback, useEffect } from "react";
import UploadIcon from "../../../assets/images/icons/catering-icons/upload-icon.svg";
import ArticleIcon from "@mui/icons-material/Article";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@mui/material";
import PropTypes from "prop-types";

export default function ImageUploader({ uploadedFiles, setUploadedFiles }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_FILES = 4;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

  // Initialize uploadedFiles if not provided
  const [localUploadedFiles, setLocalUploadedFiles] = useState([]);
  const filesState = uploadedFiles || localUploadedFiles;
  const setFilesState = setUploadedFiles || setLocalUploadedFiles;

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return "Please upload only PDF, PNG, or JPG files";
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File size must be less than 5MB";
    }
    return null;
  };

  const simulateUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setFilesState((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, progress: 100, status: "completed" } : f))
        );
      } else {
        setFilesState((prev) => prev.map((f) => (f.id === fileId ? { ...f, progress } : f)));
      }
    }, 200);
  };

  const handleFiles = useCallback(
    (files) => {
      const fileArray = Array.from(files);
      const remainingSlots = MAX_FILES - filesState.length;

      if (fileArray.length > remainingSlots) {
        alert(
          `You can only upload ${remainingSlots} more file(s). Maximum ${MAX_FILES} files allowed.`
        );
        return;
      }

      fileArray.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          alert(error);
          return;
        }

        const fileId = Math.random().toString(36).substr(2, 9);
        const newFile = {
          id: fileId,
          file,
          progress: 0,
          status: "uploading",
        };

        setFilesState((prev) => [...prev, newFile]);
        simulateUpload(fileId);
      });
    },
    [filesState.length, setFilesState]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFiles(files);
      }
    },
    [handleFiles]
  );

  const handleFileSelect = useCallback(
    (e) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFiles(files);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [handleFiles]
  );

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (fileId) => {
    setFilesState((prev) => prev.filter((f) => f.id !== fileId));
  };

  const canUploadMore = filesState.length < MAX_FILES;

  return (
    <div className="w-full mx-auto space-y-4">
      {canUploadMore && (
        <div
          className={`
            relative border h-[259px] flex items-center justify-center flex-col border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${
              isDragOver
                ? "border-[#238856] bg-green-200/30"
                : "border-[#D0D5DD] bg-[#F9FAFB] hover:border-gray-400"
            }
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleFileSelect}
            className="hidden"
          />

          <img src={UploadIcon} className="mb-3" />

          <div className="">
            <p className="text-[#238856] font-semibold text-sm">
              Click to upload receipt{" "}
              <span className="text-[#535862] font-normal">or drag and drop</span>
            </p>
            <p className="text-gray-400 text-xs mt-[2px]">PDF, PNG, JPG (max. 5mb)</p>
            <p className="text-gray-400 text-xs mt-[6px]">
              {filesState.length}/{MAX_FILES} files uploaded
            </p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {filesState.map((uploadedFile) => (
          <div key={uploadedFile.id} className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 rounded p-1.5 flex items-center justify-center">
                  <ArticleIcon className="h-3 w-3 text-green-600 m-0 p-0" />
                </div>

                <span className="text-sm font-medium text-gray-700">{uploadedFile.file.name}</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadedFile.progress}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {uploadedFile.status === "completed"
                    ? "Uploaded...100%"
                    : `Uploading...${Math.round(uploadedFile.progress)}%`}
                </span>
                {uploadedFile.status === "completed" && (
                  <button
                    onClick={() => removeFile(uploadedFile.id)}
                    className="text-gray-500 font-medium text-sm hover:bg-red-100/30 hover:text-red-500 px-2 py-1 h-auto"
                  >
                    <DeleteIcon className="h-3 w-3 mr-2 -mt-[2px]" />
                    Delete Receipt
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {!canUploadMore && (
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            Maximum {MAX_FILES} files uploaded. Remove a file to upload more.
          </p>
        </div>
      )}
    </div>
  );
}

ImageUploader.propTypes = {
  uploadedFiles: PropTypes.any,
  setUploadedFiles: PropTypes.func.isRequired,
};
